/** Variant matrix wizard — Saleor-style генерация всех комбинаций атрибутов.
 *
 * UX:
 *  1. Пользователь добавляет N групп опций (пример: Цвет=[красный, синий],
 *     Размер=[M, L, XL])
 *  2. Wizard рендерит превью матрицы (red×M / red×L / red×XL / blue×M ...)
 *  3. По каждой строке: SKU (auto-gen), Название, Доплата, Остаток, checkbox
 *  4. Submit → N последовательных POST /products/{id}/variants
 *
 * SKU auto-gen: <base>-<slugged-combo>, например "SOFA-001-red-m".
 * Название auto-gen: "Цвет: красный, Размер: M".
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Wand2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { PriceInput } from "@/components/shared/PriceInput";

interface Props {
  productId: number;
  baseSku: string;
  open: boolean;
  onClose: () => void;
}

interface OptionGroup {
  id: string;
  name: string;
  values: string[];
}

interface MatrixRow {
  key: string;
  combo: { name: string; value: string }[];
  // Editable
  sku: string;
  variantName: string;
  extra_price: number;
  stock_quantity: number;
  include: boolean;
}

function slugifyShort(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[ё]/g, "e")
    .replace(/[а-я]/g, (c) => {
      const map: Record<string, string> = {
        а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ж: "zh", з: "z",
        и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p",
        р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "c", ч: "ch",
        ш: "sh", щ: "shch", ы: "y", э: "e", ю: "yu", я: "ya",
      };
      return map[c] ?? c;
    })
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 8);
}

function cartesian(arrays: string[][]): string[][] {
  if (arrays.length === 0) return [];
  return arrays.reduce<string[][]>(
    (acc, curr) =>
      acc.flatMap((existing) => curr.map((val) => [...existing, val])),
    [[]],
  );
}

export function VariantMatrixWizard({
  productId,
  baseSku,
  open,
  onClose,
}: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [groups, setGroups] = useState<OptionGroup[]>([
    { id: crypto.randomUUID(), name: "Цвет", values: [] },
  ]);
  const [valueInput, setValueInput] = useState<Record<string, string>>({});
  const [matrixOverrides, setMatrixOverrides] = useState<
    Record<string, Partial<MatrixRow>>
  >({});

  // Generate matrix on the fly
  const matrix = useMemo<MatrixRow[]>(() => {
    const valid = groups.filter((g) => g.name.trim() && g.values.length > 0);
    if (valid.length === 0) return [];
    const combos = cartesian(valid.map((g) => g.values));

    return combos.map((vals) => {
      const combo = valid.map((g, i) => ({
        name: g.name,
        value: vals[i] ?? "",
      }));
      const key = combo.map((c) => `${c.name}=${c.value}`).join("|");
      const sku = `${baseSku || "VAR"}-${combo
        .map((c) => slugifyShort(c.value))
        .join("-")}`;
      const variantName = combo.map((c) => `${c.name}: ${c.value}`).join(", ");
      const override = matrixOverrides[key] ?? {};
      return {
        key,
        combo,
        sku: override.sku ?? sku,
        variantName: override.variantName ?? variantName,
        extra_price: override.extra_price ?? 0,
        stock_quantity: override.stock_quantity ?? 0,
        include: override.include ?? true,
      };
    });
  }, [groups, baseSku, matrixOverrides]);

  const includedCount = matrix.filter((r) => r.include).length;

  const generateMutation = useMutation({
    mutationFn: async () => {
      const rows = matrix.filter((r) => r.include);
      let ok = 0;
      let fail = 0;
      for (const r of rows) {
        try {
          await api.post(`/products/${productId}/variants`, {
            sku: r.sku,
            name: r.variantName,
            extra_price: r.extra_price,
            stock_quantity: r.stock_quantity,
            is_active: true,
          });
          ok++;
        } catch (e) {
          console.error("[variant-matrix] failed", r, e);
          fail++;
        }
      }
      return { ok, fail };
    },
    onSuccess: ({ ok, fail }) => {
      toast.success(`Создано ${ok}${fail ? `, ошибок ${fail}` : ""}`);
      queryClient.invalidateQueries({
        queryKey: ["admin-product", productId],
      });
      onClose();
      setGroups([{ id: crypto.randomUUID(), name: "Цвет", values: [] }]);
      setMatrixOverrides({});
    },
  });

  const addGroup = () =>
    setGroups((prev) => [
      ...prev,
      { id: crypto.randomUUID(), name: "", values: [] },
    ]);
  const removeGroup = (id: string) =>
    setGroups((prev) => prev.filter((g) => g.id !== id));
  const updateGroup = (id: string, patch: Partial<OptionGroup>) =>
    setGroups((prev) =>
      prev.map((g) => (g.id === id ? { ...g, ...patch } : g)),
    );

  const addValue = (id: string) => {
    const v = valueInput[id]?.trim();
    if (!v) return;
    const group = groups.find((g) => g.id === id);
    if (!group || group.values.includes(v)) return;
    updateGroup(id, { values: [...group.values, v] });
    setValueInput((prev) => ({ ...prev, [id]: "" }));
  };

  const removeValue = (id: string, value: string) => {
    const group = groups.find((g) => g.id === id);
    if (!group) return;
    updateGroup(id, { values: group.values.filter((v) => v !== value) });
  };

  const patchRow = (key: string, patch: Partial<MatrixRow>) =>
    setMatrixOverrides((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" />
            Сгенерировать варианты по матрице
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Опции */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Опции</h3>
            <Button size="sm" variant="outline" onClick={addGroup}>
              <Plus className="w-3.5 h-3.5 mr-1" />
              Добавить опцию
            </Button>
          </div>

          <div className="space-y-2">
            {groups.map((g) => (
              <div
                key={g.id}
                className="border rounded p-3 space-y-2 bg-muted/10"
              >
                <div className="flex items-center gap-2">
                  <Input
                    value={g.name}
                    onChange={(e) =>
                      updateGroup(g.id, { name: e.target.value })
                    }
                    placeholder="Название опции (Цвет, Размер, ...)"
                    className="flex-1"
                  />
                  {groups.length > 1 && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => removeGroup(g.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {g.values.map((v) => (
                    <span
                      key={v}
                      className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs"
                    >
                      {v}
                      <button
                        type="button"
                        onClick={() => removeValue(g.id, v)}
                        className="rounded-full hover:bg-background"
                        aria-label={`Убрать ${v}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Новое значение (Enter — добавить)"
                    value={valueInput[g.id] ?? ""}
                    onChange={(e) =>
                      setValueInput((prev) => ({
                        ...prev,
                        [g.id]: e.target.value,
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addValue(g.id);
                      }
                    }}
                  />
                  <Button size="sm" onClick={() => addValue(g.id)}>
                    Добавить
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Step 2: Матрица */}
        {matrix.length > 0 && (
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">
                Матрица ({includedCount} из {matrix.length} будут созданы)
              </h3>
            </div>
            <div className="border rounded-md max-h-96 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Название</TableHead>
                    <TableHead className="w-32">Доплата</TableHead>
                    <TableHead className="w-20">Остаток</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matrix.map((row) => (
                    <TableRow
                      key={row.key}
                      className={row.include ? "" : "opacity-50"}
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={row.include}
                          onChange={(e) =>
                            patchRow(row.key, { include: e.target.checked })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={row.sku}
                          onChange={(e) =>
                            patchRow(row.key, { sku: e.target.value })
                          }
                          className="h-8 text-xs font-mono"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={row.variantName}
                          onChange={(e) =>
                            patchRow(row.key, { variantName: e.target.value })
                          }
                          className="h-8 text-xs"
                        />
                      </TableCell>
                      <TableCell>
                        <PriceInput
                          value={row.extra_price}
                          onChange={(extra_price) =>
                            extra_price != null &&
                            patchRow(row.key, { extra_price })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min={0}
                          value={row.stock_quantity}
                          onChange={(e) =>
                            patchRow(row.key, {
                              stock_quantity: Number(e.target.value) || 0,
                            })
                          }
                          className="h-8 text-xs"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        )}

        {matrix.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4 border rounded">
            Добавьте хотя бы одну опцию с значениями, чтобы увидеть матрицу.
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => generateMutation.mutate()}
            disabled={includedCount === 0 || generateMutation.isPending}
          >
            {generateMutation.isPending
              ? "Создаю..."
              : `Создать ${includedCount} вариант(ов)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
